import {useEffect, useState} from "react";

export type BuildMetadata = {
	version: string;
	timestamp: string;
	appVersion?: string;
};

/**
 * Loads `/version.json` produced by `scripts/generate-version.js` in CI (and optionally locally).
 * Returns null if the file is missing (e.g. local dev without running the script).
 */
export function useBuildMetadata(): BuildMetadata | null {
	const [data, setData] = useState<BuildMetadata | null>(null);

	useEffect(() => {
		let cancelled = false;
		const base = process.env.PUBLIC_URL ?? "";
		const url = `${base.replace(/\/$/, "")}/version.json`;

		fetch(url)
			.then((res) => (res.ok ? res.json() : null))
			.then((json: BuildMetadata | null) => {
				if (!cancelled && json && typeof json.version === "string") {
					setData(json);
				}
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, []);

	return data;
}

/**
 * Single muted line for settings/debug, e.g. "v0.1.0 · Build 20250415.3".
 */
export function formatBuildMetadataLine(meta: BuildMetadata | null): string | null {
	if (!meta) return null;
	const parts: string[] = [];
	if (meta.appVersion) {
		parts.push(`v${meta.appVersion}`);
	}
	if (meta.version) {
		parts.push(`Build ${meta.version}`);
	}
	return parts.length ? parts.join(" · ") : null;
}
